import React, { createContext, useCallback, useMemo, useState } from 'react';

import { AxiosResponse } from 'axios';
import { Dispatch } from 'redux';
import { useDispatch } from 'react-redux';

import {
  createStaffInviteRequest,
  deleteInviteRequest,
  getInvitesRequest,
  getSentInvitesRequest,
  getUserInviteRequest,
  redeemActiveAdminInvitesRequest,
  redeemPublicInviteTokenRequest,
  redeemPublicInviteToTeamTokenRequest,
  resendInviteRequest,
  retryInviteItemRequest,
} from '@vyce/core/src/api/invites';
import {
  GetInvitesRequest,
  RedeemActiveInviteCompany,
  RetryInviteItemResponse,
  SendStaffInviteRequest,
  StatusMessage,
} from '@vyce/core/src/api/types';
import { deriveInviteModules, Starters } from '@vyce/core/src/types';
import { getFromLS } from '@vyce/core/src/utils/local-storage';

import { ActionType } from './action-types';
import { UserAction } from './actions/user';
import { HelperAction } from './actions/helper';
import { refreshUserAndDispatch } from './action-creators/user';

type AppDispatch = Dispatch<UserAction | HelperAction>;

const hasToken = (): boolean => !!getFromLS('token');

const fetchActiveEmployeeInvites = async (dispatch: Dispatch<UserAction>): Promise<void> => {
  if (!hasToken()) return;
  try {
    const res = await getSentInvitesRequest();
    const items = res.data?.items ?? [];
    dispatch({ type: ActionType.SET_EMPLOYEE_ACTIVE_INVITES, payload: items });
    dispatch({ type: ActionType.SET_INVITE_MODULES, payload: deriveInviteModules(items) });
  } catch (e) {
    console.error(e);
  }
};

interface InvitesContextValue {
  activeInvites: RedeemActiveInviteCompany[];
  activeInvitesDialogOpen: boolean;
  setActiveInvitesDialogOpen: (open: boolean) => void;
  redeemActiveAdminInvites: () => Promise<RedeemActiveInviteCompany[]>;
  refreshActiveInvites: () => Promise<void>;
  prepareOnboarding: () => Promise<void>;
  isPreparingOnboarding: boolean;

  getInvites: (args: GetInvitesRequest) => Promise<AxiosResponse<any>>;
  createStaffInvite: (
    companyId: string,
    payload: SendStaffInviteRequest
  ) => Promise<AxiosResponse<StatusMessage>>;
  resendInvite: (companyId: string, inviteId: string) => Promise<AxiosResponse<StatusMessage>>;
  deleteInvite: (companyId: string, inviteId: string) => Promise<AxiosResponse<StatusMessage>>;
  retryInviteItem: (
    companyId: string,
    inviteId: string,
    itemId: string
  ) => Promise<AxiosResponse<RetryInviteItemResponse>>;

  getUserInvite: (inviteToken: string) => Promise<AxiosResponse<any>>;
  redeemPublicInviteToken: (
    token: string,
    inviteToken: string,
    starters: Starters
  ) => Promise<AxiosResponse<any>>;
  redeemPublicInviteToTeamToken: (inviteToken: string) => Promise<AxiosResponse<any>>;
}

const noopAsync = async () => {
  throw new Error('InvitesContext not initialised');
};

export const InvitesContext = createContext<InvitesContextValue>({
  activeInvites: [],
  activeInvitesDialogOpen: false,
  setActiveInvitesDialogOpen: () => {},
  redeemActiveAdminInvites: async () => [],
  refreshActiveInvites: async () => {},
  prepareOnboarding: async () => {},
  isPreparingOnboarding: false,
  getInvites: noopAsync as any,
  createStaffInvite: noopAsync as any,
  resendInvite: noopAsync as any,
  deleteInvite: noopAsync as any,
  retryInviteItem: noopAsync as any,
  getUserInvite: noopAsync as any,
  redeemPublicInviteToken: noopAsync as any,
  redeemPublicInviteToTeamToken: noopAsync as any,
});

interface ProviderProps {
  children: React.ReactNode;
}

export const InvitesProvider: React.FC<ProviderProps> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [activeInvites, setActiveInvites] = useState<RedeemActiveInviteCompany[]>([]);
  const [activeInvitesDialogOpen, setActiveInvitesDialogOpen] = useState<boolean>(false);
  const [isPreparingOnboarding, setIsPreparingOnboarding] = useState<boolean>(true);

  const redeemActiveAdminInvites = useCallback(async () => {
    if (!hasToken()) return [];
    try {
      const res = await redeemActiveAdminInvitesRequest();
      const companies = res.data || [];
      if (companies.length) {
        setActiveInvites(companies);
        setActiveInvitesDialogOpen(true);
        await refreshUserAndDispatch(dispatch);
      }
      return companies;
    } catch (e) {
      console.error(e);
      return [];
    }
  }, [dispatch]);

  const refreshActiveInvites = useCallback(() => fetchActiveEmployeeInvites(dispatch), [dispatch]);

  const prepareOnboarding = useCallback(async () => {
    setIsPreparingOnboarding(true);
    try {
      await fetchActiveEmployeeInvites(dispatch);
    } finally {
      setIsPreparingOnboarding(false);
    }
  }, [dispatch]);

  const getInvites = useCallback((args: GetInvitesRequest) => getInvitesRequest(args), []);
  const createStaffInvite = useCallback(
    (companyId: string, payload: SendStaffInviteRequest) => createStaffInviteRequest(companyId, payload),
    []
  );
  const resendInvite = useCallback(
    (companyId: string, inviteId: string) => resendInviteRequest(companyId, inviteId),
    []
  );
  const deleteInvite = useCallback(
    (companyId: string, inviteId: string) => deleteInviteRequest(companyId, inviteId),
    []
  );
  const retryInviteItem = useCallback(
    (companyId: string, inviteId: string, itemId: string) =>
      retryInviteItemRequest(companyId, inviteId, itemId),
    []
  );

  const getUserInvite = useCallback((inviteToken: string) => getUserInviteRequest(inviteToken), []);
  const redeemPublicInviteToken = useCallback(
    (token: string, inviteToken: string, starters: Starters) =>
      redeemPublicInviteTokenRequest(token, inviteToken, starters),
    []
  );
  const redeemPublicInviteToTeamToken = useCallback(
    (inviteToken: string) => redeemPublicInviteToTeamTokenRequest(inviteToken),
    []
  );

  const value = useMemo<InvitesContextValue>(
    () => ({
      activeInvites,
      activeInvitesDialogOpen,
      setActiveInvitesDialogOpen,
      redeemActiveAdminInvites,
      refreshActiveInvites,
      prepareOnboarding,
      isPreparingOnboarding,
      getInvites,
      createStaffInvite,
      resendInvite,
      deleteInvite,
      retryInviteItem,
      getUserInvite,
      redeemPublicInviteToken,
      redeemPublicInviteToTeamToken,
    }),
    [
      activeInvites,
      activeInvitesDialogOpen,
      redeemActiveAdminInvites,
      refreshActiveInvites,
      prepareOnboarding,
      isPreparingOnboarding,
      getInvites,
      createStaffInvite,
      resendInvite,
      deleteInvite,
      retryInviteItem,
      getUserInvite,
      redeemPublicInviteToken,
      redeemPublicInviteToTeamToken,
    ]
  );

  return <InvitesContext.Provider value={value}>{children}</InvitesContext.Provider>;
};
